import icons from '@/assets/icons';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import { Badge, Box, Divider, Flex, Link, Text } from '@cmpsr/components';
import { NavLink } from 'react-router-dom';
import useTestDetailsHeaderController, { SectionRefs } from './test-details-header.controller';

type TestDetailsHeaderProps = {
  testTitle: string;
  testType: string;
  navItems: string[];
  icon: keyof typeof icons;
  onRefsCreated: (refs: SectionRefs) => void;
};

function TestDetailsHeader({ testTitle, testType, navItems, icon, onRefsCreated }: TestDetailsHeaderProps) {
  const { activeItem, handleClick, isMobile } = useTestDetailsHeaderController(onRefsCreated, navItems);

  return (
    <Box mb={5}>
      <Flex justifyContent="space-between" alignItems="center">
        <IconCatch
          icon={icon}
          size={isMobile ? 'xl' : 'xxl'}
          background="gradient-01"
          fill="white"
          filter="brightness(0) invert(1)"
        />
        <Badge variant="subtle" py={{ base: 1, md: 2 }} px={2} borderRadius="radii-1" textTransform="none">
          <Text
            variant="text-body-meta-medium"
            maxW={{ base: '200px', md: '370px', lg: '450px' }}
            fontWeight={600}
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {testType}
          </Text>
        </Badge>
      </Flex>
      <Text as="h1" variant="text-header-4XL" mt={6} mb={2}>
        {testTitle}
      </Text>
      <Flex
        textTransform="uppercase"
        gap={8}
        mt={6}
        overflow="auto"
        css={{ '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {navItems.map((item) => (
          <Link
            key={item}
            as={NavLink}
            to={`#${item}`}
            onClick={handleClick(item as keyof SectionRefs)}
            borderBottom="4px solid"
            borderColor={activeItem === item ? 'text-primary' : 'transparent'}
            borderRadius="0"
            pt={1}
            pb={2}
            fontSize={{ base: '14px', lg: '16px' }}
          >
            {item}
          </Link>
        ))}
      </Flex>
      <Divider />
    </Box>
  );
}

export default TestDetailsHeader;
